import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_nuxtvuexroutersync_ee50b628 from 'nuxt_plugin_nuxtvuexroutersync_ee50b628' // Source: ./nuxt-vuex-router-sync.js (mode: 'client')
import nuxt_plugin_axios_59fee404 from 'nuxt_plugin_axios_59fee404' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_ga_fb0a2534 from 'nuxt_plugin_ga_fb0a2534' // Source: ../plugins/ga.js (mode: 'client')
import nuxt_plugin_updateisiframeopen_61cb6b1a from 'nuxt_plugin_updateisiframeopen_61cb6b1a' // Source: ../plugins/update-is-iframe-open.js (mode: 'client')
import nuxt_plugin_globalfilters_775d0cc7 from 'nuxt_plugin_globalfilters_775d0cc7' // Source: ../plugins/global-filters (mode: 'all')
import nuxt_plugin_keepquerystring_25264d70 from 'nuxt_plugin_keepquerystring_25264d70' // Source: ../plugins/keep-query-string (mode: 'all')
import nuxt_plugin_airbrake_6c0b54d2 from 'nuxt_plugin_airbrake_6c0b54d2' // Source: ../plugins/airbrake (mode: 'all')
import nuxt_plugin_purcheasesdk_c8e6ff52 from 'nuxt_plugin_purcheasesdk_c8e6ff52' // Source: ../plugins/purchease-sdk (mode: 'client')
import nuxt_plugin_vueinfiniteloading_5f280597 from 'nuxt_plugin_vueinfiniteloading_5f280597' // Source: ../plugins/vue-infinite-loading.js (mode: 'client')
import nuxt_plugin_vuequaggajs_1ed462ca from 'nuxt_plugin_vuequaggajs_1ed462ca' // Source: ../plugins/vue-quaggajs (mode: 'client')
import nuxt_plugin_vuescrollto_53dc20e5 from 'nuxt_plugin_vuescrollto_53dc20e5' // Source: ../plugins/vue-scrollto (mode: 'client')
import nuxt_plugin_vue2clickoutside_3f47abb5 from 'nuxt_plugin_vue2clickoutside_3f47abb5' // Source: ../plugins/vue2-click-outside (mode: 'all')
import nuxt_plugin_webrtcadapter_595c85f6 from 'nuxt_plugin_webrtcadapter_595c85f6' // Source: ../plugins/webrtc-adapter (mode: 'client')
import nuxt_plugin_veevalidate_6e5ad03a from 'nuxt_plugin_veevalidate_6e5ad03a' // Source: ../plugins/vee-validate (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule
const baseStoreOptions = { preserveState: process.client }

function registerModule (path, rawModule, options = {}) {
  return originalRegisterModule.call(this, path, rawModule, { ...baseStoreOptions, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"fidmarques-web-widget","meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"description","name":"description","content":"Web Widget to manage your FidMarques account"}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"},{"rel":"preconnect","href":"https:\u002F\u002Ffonts.googleapis.com"},{"rel":"preconnect","href":"https:\u002F\u002Ffonts.gstatic.com","crossorigin":true},{"rel":"stylesheet","href":"https:\u002F\u002Ffonts.googleapis.com\u002Fcss2?family=Open+Sans:wght@300;400;700&family=Oswald:wght@300;400;700&display=swap"}],"style":[],"script":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.client && typeof nuxt_plugin_nuxtvuexroutersync_ee50b628 === 'function') {
    await nuxt_plugin_nuxtvuexroutersync_ee50b628(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_59fee404 === 'function') {
    await nuxt_plugin_axios_59fee404(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_ga_fb0a2534 === 'function') {
    await nuxt_plugin_ga_fb0a2534(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_updateisiframeopen_61cb6b1a === 'function') {
    await nuxt_plugin_updateisiframeopen_61cb6b1a(app.context, inject)
  }

  if (typeof nuxt_plugin_globalfilters_775d0cc7 === 'function') {
    await nuxt_plugin_globalfilters_775d0cc7(app.context, inject)
  }

  if (typeof nuxt_plugin_keepquerystring_25264d70 === 'function') {
    await nuxt_plugin_keepquerystring_25264d70(app.context, inject)
  }

  if (typeof nuxt_plugin_airbrake_6c0b54d2 === 'function') {
    await nuxt_plugin_airbrake_6c0b54d2(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_purcheasesdk_c8e6ff52 === 'function') {
    await nuxt_plugin_purcheasesdk_c8e6ff52(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueinfiniteloading_5f280597 === 'function') {
    await nuxt_plugin_vueinfiniteloading_5f280597(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuequaggajs_1ed462ca === 'function') {
    await nuxt_plugin_vuequaggajs_1ed462ca(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuescrollto_53dc20e5 === 'function') {
    await nuxt_plugin_vuescrollto_53dc20e5(app.context, inject)
  }

  if (typeof nuxt_plugin_vue2clickoutside_3f47abb5 === 'function') {
    await nuxt_plugin_vue2clickoutside_3f47abb5(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_webrtcadapter_595c85f6 === 'function') {
    await nuxt_plugin_webrtcadapter_595c85f6(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_veevalidate_6e5ad03a === 'function') {
    await nuxt_plugin_veevalidate_6e5ad03a(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, (err) => {
        // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
        if (!err._isRouter) return reject(err)
        if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
